import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  HeartHandshake,
  Users,
  Megaphone,
  Handshake,
  ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WAYS = [
  {
    key: "donor",
    title: "Give",
    icon: HeartHandshake,
    body: "A monthly gift covers a mother's therapy session, school fees or seed grant.",
  },
  {
    key: "volunteer",
    title: "Volunteer",
    icon: Users,
    body: "Facilitate a circle, tutor a child or join our outreach weekends.",
  },
  {
    key: "mentor",
    title: "Mentor",
    icon: Handshake,
    body: "Walk with a mother 1:1 — share your skills, time and belief in her.",
  },
  {
    key: "partner",
    title: "Partner",
    icon: Megaphone,
    body: "Schools, clinics and businesses: let's build inclusion together.",
  },
];

export default function GetInvolved({ onDonate }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "volunteer",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/volunteer`, form);
      toast.success("Thank you — we'll be in touch within 48 hours.");
      setForm({ name: "", email: "", phone: "", interest: "volunteer", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="get-involved"
      data-testid="get-involved-section"
      className="relative py-20 lg:py-28 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-forest font-semibold">
              Get involved
            </div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-[1.05]">
              Stand with a mother.{" "}
              <span className="italic text-brand-forest">Today.</span>
            </h2>
            <p className="mt-5 text-brand-muted text-lg leading-relaxed">
              Whether it&apos;s an hour a week or a one-time gift, you become part of
              a network that refuses to let any mother fall through the cracks.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {WAYS.map((w, i) => (
                <motion.div
                  key={w.key}
                  data-testid={`involve-card-${w.key}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="rounded-2xl border border-brand-border bg-white p-5 hover-lift"
                >
                  <div className="h-10 w-10 rounded-xl bg-brand-forest/10 text-brand-forest flex items-center justify-center">
                    <w.icon className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-serif text-lg text-brand-ink">
                    {w.title}
                  </div>
                  <p className="mt-1 text-sm text-brand-muted leading-relaxed">
                    {w.body}
                  </p>
                </motion.div>
              ))}
            </div>

            <button
              onClick={onDonate}
              data-testid="get-involved-donate-button"
              className="btn-primary mt-8"
            >
              <HeartHandshake className="h-5 w-5" />
              Donate Now
            </button>
          </div>

          <motion.form
            onSubmit={handleSubmit}
            data-testid="volunteer-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 rounded-[24px] bg-white ring-1 ring-brand-border p-7 lg:p-10"
          >
            <div className="font-serif text-2xl lg:text-3xl text-brand-ink">
              Tell us how you&apos;d like to help
            </div>
            <p className="mt-2 text-sm text-brand-muted">
              We&apos;ll respond within 48 hours.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                  Full name
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  data-testid="volunteer-name-input"
                  required
                  className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                  Email
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  data-testid="volunteer-email-input"
                  required
                  className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                  Phone (optional)
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  data-testid="volunteer-phone-input"
                  className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                  placeholder="+255 ..."
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                  I&apos;d like to
                </label>
                <select
                  name="interest"
                  value={form.interest}
                  onChange={handleChange}
                  data-testid="volunteer-interest-select"
                  className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                >
                  <option value="volunteer">Volunteer my time</option>
                  <option value="mentor">Mentor a mother</option>
                  <option value="donor">Become a donor</option>
                  <option value="partner">Partner with the Foundation</option>
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                A short note
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                data-testid="volunteer-message-input"
                rows={4}
                className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                placeholder="Tell us about your skills, time, or interest."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="volunteer-submit-button"
              className="btn-primary mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending..." : "Send my application"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
